import { analyticsAccountType } from './analyticsAccountType';
import { isDotCom } from '../site';
import { trackUmamiEvent } from './umami';

export type FrameGiftEvent = 'gift_frame_offer_viewed' | 'gift_frame_offer_clicked' |
  'gift_frame_checkout_viewed' | 'gift_frame_checkout_clicked' | 'gift_frame_checkout_started' |
  'gift_frame_checkout_failed' | 'gift_frame_received_clicked' | 'gift_frame_thank_clicked' |
  'gift_frame_thank_sent';
export type FrameGiftContext = { button_variant?: 'primary' | 'secondary'; message_type?: 'predefined' | 'custom' | 'empty'; first_open?: boolean; test_price?: boolean; flow_id?: string; request_id?: string };
export type FrameGiftOffer = {
  experiment?: string; group?: 'G1' | 'G2' | null; site?: 'ru' | 'com';
  amountMinor?: number; currency?: string; testPrice?: boolean;
};
function umamiContext(offer?: FrameGiftOffer | null) {
  return {
    account_type: analyticsAccountType(), site: offer?.site ?? (isDotCom() ? 'com' : 'ru'),
    price: typeof offer?.amountMinor === 'number' ? offer.amountMinor / 100 : null,
    currency: offer?.currency ?? 'unknown', experiment: offer?.experiment ?? 'unknown',
    variant: offer?.group ?? 'unknown', ...(offer?.testPrice ? { test_payment: true } : {}),
  };
}
/** Random flow/request IDs stay internal. No personal data is sent to Umami. */
export function trackFrameGift(event: FrameGiftEvent, context: FrameGiftContext = {}, offer?: FrameGiftOffer | null) {
  try { void Promise.resolve(trackUmamiEvent(event, { ...Object.fromEntries(Object.entries(context).filter(([key]) => !['flow_id', 'request_id'].includes(key))), ...umamiContext(offer) })).catch(() => {}); } catch { /* Best effort. */ }
  if (event === 'gift_frame_checkout_clicked' || event === 'gift_frame_checkout_failed') return;
  try { void fetch('/api/analytics/events', {
    method: 'POST', credentials: 'same-origin', keepalive: true,
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ eventName: event, context }),
  }).catch(() => {}); } catch { /* Best effort. */ }
}

/** Carousel interest is deliberately Umami-only, outside the purchase funnel. */
export function trackFrameGiftCarousel(offer?: FrameGiftOffer | null) {
  try { void Promise.resolve(trackUmamiEvent('gift_frame_carousel_clicked', umamiContext(offer))).catch(() => {}); } catch { /* Best effort. */ }
}

const reportedGiftPayments = new Set<number>();
/** Called only after server confirmation. Payment IDs stay in this browser tab. */
export function trackFrameGiftSuccess(paymentId: number, offer?: FrameGiftOffer | null) {
  if (!Number.isSafeInteger(paymentId) || paymentId <= 0 || reportedGiftPayments.has(paymentId)) return;
  const key = `santa:gift-frame-success:${paymentId}`;
  try { if (window.sessionStorage.getItem(key)) return; } catch { /* Storage may be unavailable. */ }
  reportedGiftPayments.add(paymentId);
  try { window.sessionStorage.setItem(key, '1'); } catch { /* The in-memory guard still applies. */ }
  try { void Promise.resolve(trackUmamiEvent('gift_frame_checkout_succeeded', umamiContext(offer))).catch(() => {}); } catch { /* Analytics must not interrupt success. */ }
}
